import React from 'react';
import {
  Button,
  FormControl,
  makeStyles,
  MenuItem,
  Modal,
  Select,
  Typography,
} from '@material-ui/core';
import MaterialTable from 'material-table';
import { Alert, AlertTitle } from '@material-ui/lab';

import useStyles from '../styles/forms';
import MTLocalization from '../utils/MTLocalization';
import axios from '../utils/axios';
import Loader from '../layout/Loader';
import ServerResponseAlert from './ServerResponseAlert';
import TreeBranchMultiselect from './TreeBranchMultiselect';
import { AccessRightBranchEditor, AccessRightBranchList } from './AccessRightBranchEditor';
import {
  ACCESS_TYPES,
  isObjectEmpty,
  SOURCES,
  SOURCE_PROVISION,
  SOURCE_TREE_DEFINITION,
} from './accessRightRuleConfig';
import {
  branchesToRules,
  isUnrestrictedBranches,
  mapAccessRightFromApi,
  normalizeBranches,
} from '../utils/accessRulesAdapter';

const findAccessRight = (existingRights, source, type) => (
  existingRights.find((right) => right.source === source && right.type === type)
);

const filterObject = (object, filteringCallback, labelCallback) => Object.keys(object)
  .filter((item) => filteringCallback(item))
  .map((objectKey) => ({
    key: objectKey,
    label: labelCallback ? labelCallback(objectKey) : object[objectKey],
  }));

const getPossibleSources = (existingRights) => filterObject(
  SOURCES,
  (sourceKey) => !findAccessRight(existingRights, sourceKey, 'read')
    || !findAccessRight(existingRights, sourceKey, 'write'),
);

const getPossibleAccessTypes = (existingRights, source) => filterObject(
  ACCESS_TYPES,
  (accessType) => {
    if (source === SOURCE_PROVISION && accessType === 'read') {
      return false;
    }

    return !findAccessRight(existingRights, source, accessType);
  },
);

const useStylesLocal = makeStyles({
  modal: {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxHeight: '90vh',
    overflow: 'scroll',
  },
  ruleList: {
    paddingLeft: '0',
    listStyleType: 'none',
  },
  ruleLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  ruleLineButton: {
    marginLeft: '1.5rem',
  },
  newRuleButton: {
    marginLeft: '-0.5rem',
  },
  branch: {
    marginBottom: '0.75rem',
  },
  select: {
    width: 'calc(100% - 1rem)',
  },
  selectSmall: {
    minWidth: '150px',
  },
  fieldFullSize: {
    flexGrow: '1',
  },
  bottomActions: {
    marginTop: '2rem !important',
  },
  headline: {
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  multiselectChip: {
    margin: '2px',
  },
  multitextInput: {
    paddingTop: '6px',
    paddingBottom: '7px !important',
  },
  ruleName: {
    textWrap: 'nowrap',
  },
  addBranchButton: {
    marginTop: '0.5rem',
    marginLeft: '-0.5rem',
  },
});

export const ConsumerAccessRightsDialog = ({
  open,
  onClose,
  consumerKey,
}) => {
  const [accessRights, setAccessRights] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dataLoadingError, setDataLoadingError] = React.useState(false);
  const [dataModificationError, setDataModificationError] = React.useState(false);

  const classes = useStylesLocal();
  const formClasses = useStyles();

  const updateBranches = (rowData, onRowDataChange, branches) => {
    onRowDataChange({
      ...rowData,
      branches,
    });
  };

  const columns = [
    {
      title: 'Zdroj',
      field: 'source',
      editable: 'onAdd',
      lookup: SOURCES,
      editComponent: ({ rowData, onRowDataChange }) => (
        <FormControl className={classes.select}>
          <Select
            name="source"
            value={rowData.source || ''}
            onChange={(e) => onRowDataChange({
              ...rowData,
              source: e.target.value,
              branches: [{}],
            })}
          >
            {getPossibleSources(accessRights).map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      width: '20%',
    },
    {
      title: 'Typ přístupu',
      field: 'type',
      editable: 'onAdd',
      lookup: ACCESS_TYPES,
      editComponent: ({ rowData, onRowDataChange }) => rowData.source ? (
        <FormControl className={classes.select}>
          <Select
            name="type"
            value={rowData.type || ''}
            onChange={(e) => onRowDataChange({
              ...rowData,
              type: e.target.value,
            })}
          >
            {getPossibleAccessTypes(accessRights, rowData.source).map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <em>
          Nejprve zvolte zdroj
        </em>
      ),
      width: '12%',
    },
    {
      title: 'Pravidla',
      field: 'branches',
      render: (rowData) => {
        if (isUnrestrictedBranches(rowData.branches)) {
          return (
            <em>
              Bez omezení
            </em>
          );
        }

        if (rowData.source === SOURCE_TREE_DEFINITION) {
          const tid = rowData.branches[0]?.tid;
          if (!tid || tid.length === 0) {
            return (<em>Bez omezení</em>);
          }

          return (<em>{tid.join(', ')}</em>);
        }

        return (
          <AccessRightBranchList
            branches={rowData.branches}
            source={rowData.source}
            classes={classes}
          />
        );
      },
      editComponent: ({ rowData, onRowDataChange }) => {
        if (!rowData.source || !rowData.type) {
          return (
            <em>
              Nejprve zvolte zdroj a typ přístupu
            </em>
          );
        }

        const branches = normalizeBranches(rowData.branches);

        if (rowData.source === SOURCE_TREE_DEFINITION) {
          const currentBranch = branches[0] || { tid: [] };

          return (
            <TreeBranchMultiselect
              className={classes.fieldFullSize}
              classes={{
                inputRoot: classes.multitextInput,
              }}
              name={`${rowData.source}-value`}
              values={currentBranch.tid || []}
              onChange={(values) => {
                updateBranches(rowData, onRowDataChange, [{ tid: values }]);
              }}
            />
          );
        }

        const updateBranch = (branchIndex, branch) => {
          const nextBranches = branches.map((item, index) => (
            index === branchIndex ? branch : item
          ));
          updateBranches(rowData, onRowDataChange, nextBranches);
        };

        const addBranch = () => {
          updateBranches(rowData, onRowDataChange, [...branches, {}]);
        };

        const removeBranch = (branchIndex) => {
          const nextBranches = branches.filter((_, index) => index !== branchIndex);
          updateBranches(rowData, onRowDataChange, normalizeBranches(nextBranches));
        };

        return (
          <>
            {branches.map((branch, branchIndex) => (
              <AccessRightBranchEditor
                key={`branch-edit-${branchIndex}`}
                branch={branch}
                source={rowData.source}
                classes={classes}
                onChange={(nextBranch) => updateBranch(branchIndex, nextBranch)}
                onRemove={branches.length > 1 ? () => removeBranch(branchIndex) : undefined}
              />
            ))}
            <div className={classes.addBranchButton}>
              <Button
                variant="outlined"
                color="primary"
                onClick={addBranch}
              >
                Přidat alternativu (NEBO)
              </Button>
            </div>
          </>
        );
      },
      width: '58%',
    },
  ];

  const loadAccessRights = async () => {
    try {
      setLoading(true);
      const result = await axios.get(`/consumer/v2/${consumerKey}/access`);
      setAccessRights(result.data.data.map((item) => mapAccessRightFromApi(item)));
    } catch (e) {
      setDataLoadingError('Data se nepodařilo načíst, zkuste to prosím později.');
    } finally {
      setLoading(false);
    }
  };

  const modifyAccessRight = async (data, dataUpdateCallback) => {
    try {
      const result = await axios.put(
        `/consumer/v2/${consumerKey}/access/${data.source}/${data.type}`,
        { rules: branchesToRules(data.branches) },
      );
      dataUpdateCallback(mapAccessRightFromApi(result.data.data));
      setDataModificationError(false);
      return result.data.data;
    } catch (e) {
      setDataModificationError(e.message);
      return false;
    }
  };

  const createAccessRight = async (data) => modifyAccessRight(data, (result) => {
    setAccessRights((prev) => [...prev, result]);
  });

  const updateAccessRight = async (data) => modifyAccessRight(data, (result) => {
    setAccessRights((prev) => prev.map((item) => {
      if (item.source === result.source && item.type === result.type) {
        return result;
      }
      return item;
    }));
  });

  const deleteAccessRight = async (data) => {
    try {
      await axios.delete(`/consumer/v2/${consumerKey}/access/${data.source}/${data.type}`);
      setAccessRights((prev) => prev.filter((item) => item.source !== data.source || item.type !== data.type));
      setDataModificationError(false);
      return true;
    } catch (e) {
      setDataModificationError(e.message);
      return false;
    }
  };

  const assignUniversalRights = async () => {
    const accessRightsResult = [];
    await Promise.all(
      Object.keys(SOURCES).map((sourceKey) => Promise.all(
        Object.keys(ACCESS_TYPES).map(async (typeKey) => modifyAccessRight({
          source: sourceKey,
          type: typeKey,
          branches: [],
        }, (result) => {
          accessRightsResult.push(result);
        })),
      )),
    );
    setAccessRights(accessRightsResult);
  };

  const validateAccessRight = (data) => {
    if (!data.source) {
      setDataModificationError('Je nutné vyplnit zdroj');
      return false;
    }
    if (!data.type) {
      setDataModificationError('Je nutné vyplnit typ přístupu');
      return false;
    }

    const branches = normalizeBranches(data.branches).filter((branch) => !isObjectEmpty(branch));

    if (data.source === SOURCE_TREE_DEFINITION) {
      if (branches.length > 1) {
        setDataModificationError('Pro trees je povolena pouze jedna sada pravidel');
        return false;
      }
      return true;
    }

    if (branches.some((branch) => Object.keys(branch).some((ruleKey) => !branch[ruleKey] || branch[ruleKey].length === 0))) {
      setDataModificationError('Je nutné vyplnit všechna vybraná filtrační kritéria nebo je z výběru odebrat');
      return false;
    }

    return true;
  };

  const cleanAccessRight = (data) => {
    const branches = (data.branches || []).filter((branch) => !isObjectEmpty(branch));
    return {
      ...data,
      branches,
    };
  };

  React.useEffect(() => {
    loadAccessRights();
  }, [consumerKey]);

  return (
    <Modal open={open} onClose={onClose}>
      <div className={`${formClasses.modalWindow} ${classes.modal}`}>
        <Typography variant="h5" component="h2" className={classes.headline}>Správa přístupových práv</Typography>
        {loading && <Loader />}
        {dataModificationError && (
          <ServerResponseAlert serverSuccess={!dataModificationError} serverReply={dataModificationError} onClose={() => setDataModificationError(false)} />
        )}
        {dataLoadingError && (
          <Alert severity="error">
            <AlertTitle>Chyba při načítání dat.</AlertTitle>
            {dataLoadingError}
          </Alert>
        )}
        {!loading && !dataLoadingError && (
          <>
            <MaterialTable
              localization={MTLocalization}
              options={{ actionsColumnIndex: -1, search: false, paging: false, addRowPosition: 'first', tableLayout: 'fixed' }}
              style={{ margin: 0, boxShadow: 'none', width: '100%', position: 'relative' }}
              className="MuiTableContainer"
              title=""
              columns={columns}
              data={accessRights}
              editable={{
                onRowAdd: getPossibleSources(accessRights).length > 0 ? (data) => new Promise((resolve, reject) => {
                  const rowData = {
                    ...data,
                    branches: normalizeBranches(data.branches),
                  };
                  const validationResult = validateAccessRight(rowData);
                  if (!validationResult) {
                    reject(dataModificationError);
                  } else {
                    const result = createAccessRight(cleanAccessRight(rowData));
                    if (result) {
                      resolve(result);
                    } else {
                      reject(dataModificationError);
                    }
                  }
                }) : undefined,
                onRowDelete: (data) => new Promise((resolve, reject) => {
                  const result = deleteAccessRight(data);
                  if (result) {
                    resolve(result);
                  } else {
                    reject(dataModificationError);
                  }
                }),
                onRowUpdate: (data) => new Promise((resolve, reject) => {
                  const rowData = {
                    ...data,
                    branches: normalizeBranches(data.branches),
                  };
                  const validationResult = validateAccessRight(rowData);
                  if (!validationResult) {
                    reject(dataModificationError);
                  } else {
                    const result = updateAccessRight(cleanAccessRight(rowData));
                    if (result) {
                      resolve(result);
                    } else {
                      reject(dataModificationError);
                    }
                  }
                }),
              }}
            />
            <Button
              className={classes.bottomActions}
              variant="outlined" color="primary" type="button"
              onClick={() => assignUniversalRights()}
            >
              Přiřadit univerzální práva
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};
